import { CalendarToday as CalendarIcon } from "@mui/icons-material";
import { Box, Button, Card, CardContent, CardHeader, Stack, Typography } from "@mui/material";
import { DateTime } from "luxon";

// User-facing strings
const STRINGS = {
  NO_MEETING_TIMES: "No meeting times scheduled for this day",
} as const;

import { AddEventTimesModal } from "../../organisms/AddEventTimesModal/AddEventTimesModal";
import { type MeetingTimeData } from "../../organisms/AddEventTimesModal/AddEventTimesModal.types";

interface MeetingTime {
  id: string;
  beginMeetingTime: string;
  endMeetingTime: string;
  seriesId?: string;
  series?: {
    id: string;
    name: string;
    description: string;
  };
}

interface SelectedDayCardProps {
  selectedDate?: DateTime;
  meetingTimes: MeetingTime[];
  selectedMeetingTimeId?: string;
  onSelectMeetingTime: (meetingTime: { id: string; startTime: string; endTime: string }) => void;
  eventId: string;
  canManage: boolean;
  onCreateMeetingTimes: (meetingTimes: MeetingTimeData[], eventId: string) => void;
  isCreatingMeetingTimes?: boolean;
  availableSeries?: { id: string; name: string; description: string }[];
}

export const SelectedDayCard = ({
  selectedDate,
  meetingTimes,
  selectedMeetingTimeId,
  onSelectMeetingTime,
  eventId,
  canManage,
  onCreateMeetingTimes,
  isCreatingMeetingTimes,
  availableSeries,
}: SelectedDayCardProps) => {
  if (!selectedDate) {
    return null;
  }

  // Filter meeting times for the selected day
  const dayMeetingTimes = meetingTimes.filter((mt) => {
    const meetingDate = DateTime.fromISO(mt.beginMeetingTime);
    return meetingDate.hasSame(selectedDate, "day");
  });

  return (
    <Card data-testid="selected-day-card">
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarIcon color="primary" />
            <Typography variant="h6" data-testid="selected-day-header" component="h6">
              {selectedDate.toFormat("EEEE, MMMM d")}
            </Typography>
          </Stack>
        }
        action={
          canManage && (
            <Box onClick={(e) => e.stopPropagation()}>
              <AddEventTimesModal
                onSubmit={onCreateMeetingTimes}
                disabled={isCreatingMeetingTimes}
                eventId={eventId}
                defaultDate={selectedDate}
                series={availableSeries}
              />
            </Box>
          )
        }
      />
      <CardContent>
        {dayMeetingTimes.length > 0 ? (
          <Stack spacing={1} data-testid="meeting-times-list">
            {dayMeetingTimes.map((mt) => {
              const startTime = DateTime.fromISO(mt.beginMeetingTime);
              const endTime = DateTime.fromISO(mt.endMeetingTime);
              const isSelected = mt.id === selectedMeetingTimeId;

              return (
                <Button
                  key={mt.id}
                  variant={isSelected ? "contained" : "outlined"}
                  color="primary"
                  onClick={() =>
                    onSelectMeetingTime({
                      endTime: mt.endMeetingTime,
                      id: mt.id,
                      startTime: mt.beginMeetingTime,
                    })
                  }
                  sx={{ justifyContent: "flex-start" }}
                  data-testid={`meeting-time-${mt.id}`}
                  data-selected={isSelected}
                >
                  {startTime.toFormat("h:mm a")} - {endTime.toFormat("h:mm a")}
                </Button>
              );
            })}
          </Stack>
        ) : (
          <Typography color="text.secondary" data-testid="no-meeting-times">
            {STRINGS.NO_MEETING_TIMES}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
